import React, { useState, useEffect, useCallback } from "react";
import { Modal, Tree, Input, Select, message } from "antd";
import PageLayout from "@components/pageLayout/PageLayout.react";
import api from "@/libs/api";
import "./roleManagement.css";

// Permission tree structure — keys must match np_power.action_code exactly
const PERMISSIONS_TREE = [
  {
    key: "_dashboard",
    title: "Dashboard",
    children: [
      { key: "dashboard:read", title: "View Dashboard" },
    ],
  },
  {
    key: "_systems",
    title: "Systems",
    children: [
      { key: "system:read", title: "View Systems" },
      { key: "system:onboard", title: "Onboard System" },
      { key: "system:offboard", title: "Offboard System" },
      { key: "system:update", title: "Update System" },
      { key: "system:export", title: "Export Systems" },
      { key: "system:approve", title: "Approve Onboard" },
    ],
  },
  {
    key: "_viewgroups",
    title: "View Groups",
    children: [
      { key: "viewgroup:read", title: "View Groups" },
      { key: "viewgroup:create", title: "Create Group" },
      { key: "viewgroup:update", title: "Update Group" },
      { key: "viewgroup:delete", title: "Delete Group" },
    ],
  },
  {
    key: "_dispatch",
    title: "Dispatch",
    children: [
      { key: "dispatch:read", title: "View Dispatch" },
      { key: "dispatch:create", title: "Create Dispatch" },
      { key: "dispatch:execute", title: "Execute Dispatch" },
      { key: "dispatchgroup:read", title: "View Dispatch Groups" },
      { key: "dispatchgroup:create", title: "Create Dispatch Group" },
      { key: "dispatchgroup:update", title: "Update Dispatch Group" },
    ],
  },
  {
    key: "_company",
    title: "Company",
    children: [
      { key: "company:read", title: "View Companies" },
      { key: "company:create", title: "Create Company" },
      { key: "company:update", title: "Update Company" },
      { key: "company:delete", title: "Delete Company" },
    ],
  },
  {
    key: "_settlements",
    title: "Settlements & Billing",
    children: [
      { key: "settlement:read", title: "View Settlements" },
      { key: "settlement:run", title: "Run Settlement" },
      { key: "ledger:read", title: "View Ledger" },
    ],
  },
  {
    key: "_reports",
    title: "Reports",
    children: [
      { key: "report:read", title: "View Reports" },
      { key: "report:generate", title: "Generate Reports" },
    ],
  },
  {
    key: "_users",
    title: "User Management",
    children: [
      { key: "user:read", title: "View Users" },
      { key: "user:create", title: "Create User" },
      { key: "user:update", title: "Update User" },
      { key: "user:delete", title: "Delete User" },
      { key: "user:approve", title: "Approve User" },
    ],
  },
  {
    key: "_roles",
    title: "Role Management",
    children: [
      { key: "role:read", title: "View Roles" },
      { key: "role:manage", title: "Manage Roles" },
    ],
  },
  {
    key: "_other",
    title: "Other",
    children: [
      { key: "ticket:read", title: "View Tickets" },
      { key: "api:read", title: "API Access" },
    ],
  },
];

const ROLE_TYPES = [
  { value: "SUPERADMIN", label: "Superadmin" },
  { value: "INTERNAL", label: "Internal" },
  { value: "PARTNER", label: "Partner" },
  { value: "AGGREGATOR", label: "Aggregator" },
];

export default function RoleManagement() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rolePermissions, setRolePermissions] = useState({});
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([
    "_dashboard", "_systems", "_viewgroups", "_dispatch", "_company",
    "_settlements", "_reports", "_users", "_roles", "_other",
  ]);
  const [newRole, setNewRole] = useState({ role_name: "", role_type: "INTERNAL", remark: "" });

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/roles");
      if (res.data.success) {
        setRoles(res.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch roles:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const fetchRolePermissions = async (roleId) => {
    try {
      const res = await api.get(`/api/roles/${encodeURIComponent(roleId)}/permissions`);
      if (res.data.success) {
        const perms = res.data.data || [];
        return perms.map((p) => p.action_code);
      }
    } catch (err) {
      console.error("Failed to fetch role permissions:", err);
    }
    return [];
  };

  const handleEditPermissions = async (role) => {
    setSelectedRole(role);
    // Always fetch fresh from API
    const perms = await fetchRolePermissions(role.id);
    setRolePermissions((prev) => ({ ...prev, [role.id]: perms }));
    setCheckedKeys(perms);
    setPermissionsModalOpen(true);
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;
    try {
      // Filter out parent group keys (start with _), send only action_codes
      const actionCodes = checkedKeys.filter((k) => !k.startsWith("_"));
      const res = await api.put(`/api/roles/${encodeURIComponent(selectedRole.id)}/permissions`, {
        permissions: actionCodes,
      });
      if (res.data.success) {
        // Clear cache so next open fetches fresh from API
        setRolePermissions((prev) => {
          const next = { ...prev };
          delete next[selectedRole.id];
          return next;
        });
        message.success(`Permissions updated for ${selectedRole.role_name}`);
        setPermissionsModalOpen(false);
        setSelectedRole(null);
        fetchRoles();
      }
    } catch (err) {
      console.error("Failed to save permissions:", err);
      message.error("Failed to save permissions");
    }
  };

  const handleCreateRole = async () => {
    if (!newRole.role_name.trim()) {
      message.error("Role name is required");
      return;
    }

    try {
      const res = await api.post("/api/roles", {
        role_name: newRole.role_name.trim(),
        role_type: newRole.role_type,
        description: newRole.remark.trim(),
      });
      if (res.data.success) {
        message.success(`Role "${newRole.role_name}" created`);
        setCreateModalOpen(false);
        setNewRole({ role_name: "", role_type: "INTERNAL", remark: "" });
        fetchRoles();
      }
    } catch (err) {
      console.error("Failed to create role:", err);
      message.error("Failed to create role");
    }
  };

  const handleDeleteRole = (role) => {
    if ((role.role_type || "") === "SUPERADMIN") {
      message.error("Cannot delete Superadmin role");
      return;
    }

    Modal.confirm({
      title: "Delete Role",
      content: `Are you sure you want to delete the role "${role.role_name}"?`,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const res = await api.delete(`/api/roles/${encodeURIComponent(role.id)}`);
          if (res.data.success) {
            message.success(`Role "${role.role_name}" deleted`);
            fetchRoles();
          }
        } catch (err) {
          console.error("Failed to delete role:", err);
          message.error("Failed to delete role");
        }
      },
    });
  };

  const getRoleTypeBadgeClass = (roleType) => {
    const type = (roleType || "").toUpperCase();
    if (type === "SUPERADMIN") return "rm-type-badge superadmin";
    if (type === "INTERNAL") return "rm-type-badge internal";
    if (type === "PARTNER") return "rm-type-badge partner";
    if (type === "AGGREGATOR") return "rm-type-badge aggregator";
    return "rm-type-badge";
  };

  const isSuperadmin = (role) => {
    return (role.role_type || "") === "SUPERADMIN";
  };

  const getPermissionCount = (role) => {
    return role.permission_count || 0;
  };

  return (
    <PageLayout active="roleManagement">
      <div className="rm-page">
        <div className="rm-header">
          <h1 className="rm-title">Role & Permission Management</h1>
          <p className="rm-subtitle">
            Manage roles and their permissions. Roles are shared templates used by all companies.
          </p>
        </div>

        <div className="rm-table-section">
          <div className="rm-table-header">
            <h2 className="rm-section-title">All Roles</h2>
            <button className="rm-btn-add" onClick={() => setCreateModalOpen(true)}>
              + Create Role
            </button>
          </div>
          {loading ? (
            <div className="rm-loading">Loading...</div>
          ) : (
            <div className="rm-table-wrapper">
              <table className="rm-table">
                <thead>
                  <tr>
                    <th>Role Name</th>
                    <th>Type</th>
                    <th>Permissions</th>
                    <th>Description</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.map((role) => (
                    <tr key={role.id}>
                      <td className="rm-td-name">{role.role_name}</td>
                      <td>
                        <span className={getRoleTypeBadgeClass(role.role_type)}>
                          {role.role_type}
                        </span>
                      </td>
                      <td>
                        <span className="rm-permission-count">
                          {isSuperadmin(role) ? "All" : `${getPermissionCount(role)} permissions`}
                        </span>
                      </td>
                      <td className="rm-td-remark">{role.description || "—"}</td>
                      <td>
                        <div className="rm-actions">
                          <button
                            className="rm-btn-permissions"
                            onClick={() => handleEditPermissions(role)}
                          >
                            Edit Permissions
                          </button>
                          {!isSuperadmin(role) && (
                            <button
                              className="rm-btn-delete"
                              onClick={() => handleDeleteRole(role)}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {roles.length === 0 && (
                    <tr>
                      <td colSpan={5} className="rm-empty">No roles found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create Role Modal */}
        <Modal
          title="Create New Role"
          open={createModalOpen}
          onOk={handleCreateRole}
          onCancel={() => {
            setCreateModalOpen(false);
            setNewRole({ role_name: "", role_type: "INTERNAL", remark: "" });
          }}
          okText="Create"
          width={500}
        >
          <div className="rm-form">
            <div className="rm-form-group">
              <label>Role Name</label>
              <Input
                value={newRole.role_name}
                onChange={(e) => setNewRole({ ...newRole, role_name: e.target.value })}
                placeholder="Enter role name"
              />
            </div>
            <div className="rm-form-group">
              <label>Role Type</label>
              <Select
                value={newRole.role_type}
                onChange={(value) => setNewRole({ ...newRole, role_type: value })}
                options={ROLE_TYPES.filter((t) => t.value !== "SUPERADMIN")}
                style={{ width: "100%" }}
              />
            </div>
            <div className="rm-form-group">
              <label>Description</label>
              <Input.TextArea
                value={newRole.remark}
                onChange={(e) => setNewRole({ ...newRole, remark: e.target.value })}
                placeholder="Optional description"
                rows={3}
              />
            </div>
          </div>
        </Modal>

        {/* Edit Permissions Modal */}
        <Modal
          title={`Edit Permissions - ${selectedRole?.role_name || ""}`}
          open={permissionsModalOpen}
          onOk={handleSavePermissions}
          onCancel={() => {
            setPermissionsModalOpen(false);
            setSelectedRole(null);
          }}
          okText="Save"
          width={600}
          bodyStyle={{ maxHeight: "60vh", overflow: "auto" }}
        >
          {selectedRole && isSuperadmin(selectedRole) ? (
            <div className="rm-superadmin-notice">
              Superadmin has all permissions and bypasses all checks. Permissions cannot be modified.
            </div>
          ) : (
            <div className="rm-permissions-tree">
              <Tree
                checkable
                expandedKeys={expandedKeys}
                onExpand={setExpandedKeys}
                checkedKeys={checkedKeys}
                onCheck={(checked) => setCheckedKeys(checked)}
                treeData={PERMISSIONS_TREE}
                selectable={false}
              />
            </div>
          )}
        </Modal>
      </div>
    </PageLayout>
  );
}
